import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/lib/auth-context";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";
import ParentHomePage from "@/pages/ParentHome";
import SacHome from "@/pages/SacHome";
import { PendingGlance } from "@/components/PendingGlance";
import { motion } from "framer-motion";
import {
  Bed, GraduationCap, Student, HandHeart, ShieldCheck, Cake, ChartLineUp,
  Receipt, Books, Sparkle, Buildings,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  RadarChart, PolarAngleAxis, PolarGrid, Radar,
  BarChart, Bar, Legend,
  LineChart, Line,
} from "recharts";
import { Link } from "react-router-dom";
import { SecurityBadge } from "@/components/SecurityBadge";

// Module-level constants — prevent the inline-object-in-prop re-render flag
// and let React skip motion remounts when our component re-renders.
const FADE_UP_INITIAL = { opacity: 0, y: 10 };
const FADE_UP_ANIMATE = { opacity: 1, y: 0 };
const ALERT_CLASS = { red: "occ-red", amber: "occ-amber", green: "occ-green" };
const ALERT_BG = {
  red: "var(--vs-danger)",
  amber: "var(--vs-warning)",
  green: "var(--vs-success)",
};
const MOTION_WIDTH_INITIAL = { width: 0 };
const MOTION_WIDTH_TRANSITION = { duration: 1.2, ease: "easeOut" };

// Shared chart configuration — extracted to module scope so React doesn't
// allocate new objects on every render (would invalidate `recharts` memo).
const CHART_TICK = { fontSize: 12, fill: "var(--vs-text-secondary)" };
const CHART_TICK_SM = { fontSize: 11, fill: "var(--vs-text-secondary)" };
const CHART_TICK_XS = { fontSize: 10, fill: "var(--vs-text-secondary)" };
const CHART_DOMAIN_PCT = [0, 100];
const CHART_BAR_RADIUS = [6, 6, 0, 0];
const CHART_AREA_STROKE_WIDTH = 2;
const CHART_LINE_DOT = { r: 3 };
const CHART_LINE_ACTIVE_DOT = { r: 5 };
const CHART_CONTAINER_STYLE = { width: "100%", height: 240 };
const RADAR_OUTER_RADIUS = 90;

function Stat({ icon: Icon, label, value, sub, "data-testid": testId }) {
  return (
    <motion.div
      initial={FADE_UP_INITIAL} animate={FADE_UP_ANIMATE}
      className="p-5 vs-card"
      data-testid={testId || `stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="eyebrow">{label}</div>
          <div className="font-display text-3xl font-semibold mt-2">{value}</div>
          {sub && <div className="text-xs text-[var(--vs-text-secondary)] mt-1">{sub}</div>}
        </div>
        <Icon size={22} weight="duotone" className="text-[var(--vs-secondary)]" />
      </div>
    </motion.div>
  );
}

function OccupancyBadge({ alert, pct }) {
  const cls = ALERT_CLASS[alert] || ALERT_CLASS.green;
  return (
    <span data-testid="occupancy-alert-badge" className={`inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-md ${cls}`}>
      {pct}% • {alert.toUpperCase()}
    </span>
  );
}

// =============== SUPER ADMIN ===============
function SuperAdminHome() {
  const [stats, setStats] = useState(null);
  const [hostels, setHostels] = useState([]);
  const [audit, setAudit] = useState([]);
  const [gender, setGender] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/admin/hostels").then((r) => setHostels(r.data)).catch(() => {}),
      api.get("/admin/audit-logs?limit=8").then((r) => setAudit(r.data)).catch(() => {}),
      api.get("/admin/reports/gender").then((r) => setGender(r.data)).catch(() => {}),
    ]);
  }, []);

  return (
    <DashboardLayout title="Trust Overview" subtitle="Aggregated KPIs across all hostels, schools and donor activity.">
      <PendingGlance />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Student} label="Active students" value={stats?.active_students ?? "—"} sub={`Total enrolled: ${stats?.students_total ?? "—"}`} />
        <Stat icon={Buildings} label="Trusts" value={stats?.trusts ?? "—"} sub="Total active tenants" data-testid="kpi-trusts" />
        <Stat icon={Bed} label="Hostels" value={stats?.hostels ?? "—"} sub={`Capacity: ${stats?.platform_capacity?.max_hostels?.toLocaleString() ?? "—"}`} />
        <Stat icon={GraduationCap} label="Schools" value={stats?.schools ?? "—"} sub={`Capacity: ${stats?.platform_capacity?.max_schools?.toLocaleString() ?? "—"}`} />
        <Stat icon={HandHeart} label="Donations captured" value={`₹${(stats?.total_donations ?? 0).toLocaleString()}`} sub={`${stats?.donors ?? 0} active donors`} />
      </div>

      {gender && (
        <div className="mt-4 grid grid-cols-3 gap-3" data-testid="super-gender-cards">
          <div className="vs-card p-4 border-l-4 border-[#3B82F6]" data-testid="super-gender-boys">
            <div className="eyebrow text-[#3B82F6]">Total Boys</div>
            <div className="font-display text-2xl mt-1">{gender.totals.boys}</div>
            <div className="text-xs text-[var(--vs-text-secondary)] mt-1">{gender.totals.total ? Math.round((gender.totals.boys / gender.totals.total) * 100) : 0}% of active students</div>
          </div>
          <div className="vs-card p-4 border-l-4 border-[#EC4899]" data-testid="super-gender-girls">
            <div className="eyebrow text-[#EC4899]">Total Girls</div>
            <div className="font-display text-2xl mt-1">{gender.totals.girls}</div>
            <div className="text-xs text-[var(--vs-text-secondary)] mt-1">{gender.totals.total ? Math.round((gender.totals.girls / gender.totals.total) * 100) : 0}% of active students</div>
          </div>
          <div className="vs-card p-4 border-l-4 border-[var(--vs-warning)]" data-testid="super-gender-other">
            <div className="eyebrow text-[var(--vs-warning)]">Other / Unspecified</div>
            <div className="font-display text-2xl mt-1">{gender.totals.other}</div>
            <Link to="/app/reports/gender" className="text-xs text-[var(--vs-secondary)] underline mt-1 inline-block">Full breakdown →</Link>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-5 vs-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Live hostel occupancy</div>
              <div className="font-display text-lg font-semibold">All hostels — color-coded</div>
            </div>
            <Link to="/app/hostels" className="text-xs underline underline-offset-4 hover:text-[var(--vs-secondary)]">Manage hostels →</Link>
          </div>
          <div className="space-y-3">
            {hostels.length === 0 && <div className="text-sm text-[var(--vs-text-secondary)]">No hostels yet.</div>}
            {hostels.map((h) => (
              <div key={h.id} data-testid={`hostel-row-${h.id}`} className="flex items-center justify-between gap-4 py-2 border-b border-[var(--vs-border)] last:border-b-0">
                <div className="min-w-0">
                  <div className="font-medium truncate">{h.name}</div>
                  <div className="text-xs text-[var(--vs-text-secondary)]">{h.location} • {h.occupied_beds}/{h.total_beds} beds</div>
                </div>
                <div className="w-44 h-2 rounded-full bg-[var(--vs-bg-subtle)] overflow-hidden">
                  <div
                    className="h-full"
                    style={{
                      width: `${Math.min(100, h.occupancy_pct || 0)}%`,
                      background: ALERT_BG[h.alert] || ALERT_BG.green,
                    }}
                  />
                </div>
                <OccupancyBadge alert={h.alert} pct={h.occupancy_pct} />
              </div>
            ))}
          </div>
        </div>

        <div className="p-5 vs-card">
          <div className="eyebrow">Platform capacity</div>
          <div className="font-display text-lg font-semibold mb-3">Engineered for national scale</div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between"><span className="text-[var(--vs-text-secondary)]">Schools</span><span className="font-mono">{stats?.platform_capacity?.max_schools?.toLocaleString() ?? "—"}</span></div>
            <div className="flex items-center justify-between"><span className="text-[var(--vs-text-secondary)]">Hostels</span><span className="font-mono">{stats?.platform_capacity?.max_hostels?.toLocaleString() ?? "—"}</span></div>
            <div className="flex items-center justify-between"><span className="text-[var(--vs-text-secondary)]">Students</span><span className="font-mono">{stats?.platform_capacity?.max_students?.toLocaleString() ?? "—"}</span></div>
          </div>
          <SecurityBadge className="mt-4" />
        </div>
      </div>

      <div className="mt-6 p-5 vs-card">
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow">Recent audit trail</div>
          <Link to="/app/audit" className="text-xs underline underline-offset-4 hover:text-[var(--vs-secondary)]">View full log →</Link>
        </div>
        <div className="space-y-2 text-sm">
          {audit.length === 0 && <div className="text-[var(--vs-text-secondary)]">No recent activity.</div>}
          {audit.map((a) => (
            <div key={a.id} className="flex items-center justify-between border-b border-[var(--vs-border)] last:border-b-0 py-2">
              <div className="min-w-0">
                <div className="font-mono text-[11px] tracking-wide uppercase text-[var(--vs-secondary)]">{a.action}</div>
                <div className="text-xs text-[var(--vs-text-secondary)] truncate">{a.actor_email} • {a.entity}{a.entity_id ? ` #${a.entity_id.slice(0, 8)}` : ""}</div>
              </div>
              <div className="text-[11px] font-mono text-[var(--vs-text-secondary)]">{new Date(a.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

// =============== HOSTEL ADMIN ===============
function HostelAdminHome() {
  const [occupancy, setOccupancy] = useState([]);
  const [students, setStudents] = useState([]);
  const [gender, setGender] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/hostel/occupancy").then((r) => setOccupancy(r.data)).catch(() => {}),
      api.get("/students").then((r) => setStudents(r.data)).catch(() => {}),
      api.get("/admin/reports/gender").then((r) => setGender(r.data)).catch(() => {}),
    ]);
  }, []);

  const me = occupancy[0];

  return (
    <DashboardLayout title={me?.name || "Hostel Dashboard"} subtitle="Real-time bed occupancy, students under your hostel.">
      {gender && (
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="hostel-gender-cards">
          <div className="vs-card p-4 border-l-4 border-[#3B82F6]" data-testid="hostel-gender-boys">
            <div className="eyebrow text-[#3B82F6]">Boys</div>
            <div className="font-display text-2xl mt-1">{gender.totals.boys}</div>
          </div>
          <div className="vs-card p-4 border-l-4 border-[#EC4899]" data-testid="hostel-gender-girls">
            <div className="eyebrow text-[#EC4899]">Girls</div>
            <div className="font-display text-2xl mt-1">{gender.totals.girls}</div>
          </div>
          <div className="vs-card p-4 border-l-4 border-[var(--vs-warning)]" data-testid="hostel-gender-other">
            <div className="eyebrow text-[var(--vs-warning)]">Other</div>
            <div className="font-display text-2xl mt-1">{gender.totals.other}</div>
          </div>
        </div>
      )}      {me && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-6 vs-card md:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow">Occupancy</div>
                <div className="font-display text-5xl font-semibold mt-2">{me.occupancy_pct}%</div>
                <div className="text-sm text-[var(--vs-text-secondary)] mt-1">{me.occupied_beds} occupied of {me.total_beds} beds</div>
              </div>
              <OccupancyBadge alert={me.alert} pct={me.occupancy_pct} />
            </div>
            <div className="mt-5 w-full h-3 rounded-full bg-[var(--vs-bg-subtle)] overflow-hidden">
              <motion.div
                initial={MOTION_WIDTH_INITIAL}
                animate={{ width: `${Math.min(100, me.occupancy_pct || 0)}%` }}
                transition={MOTION_WIDTH_TRANSITION}
                className="h-full"
                style={{ background: ALERT_BG[me.alert] || ALERT_BG.green }}
              />
            </div>
          </div>

          <Stat icon={Student} label="Active students" value={students.filter((s) => s.status === "active").length} sub="Under your hostel scope" />
        </div>
      )}

      <div className="mt-6 p-5 vs-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="eyebrow">Students</div>
            <div className="font-display text-lg font-semibold">Roster — {students.length} students</div>
          </div>
          <Link to="/app/admissions" className="text-xs px-3 py-1.5 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] hover:bg-[var(--vs-secondary-hover)]">+ New admission</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)] border-b border-[var(--vs-border)]">
                <th className="py-2">VS-ID</th><th>Name</th><th>Class</th><th>Room/Bed</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {students.slice(0, 10).map((s) => (
                <tr key={s.id} className="border-b border-[var(--vs-border)] last:border-b-0">
                  <td className="py-2.5 font-mono text-xs">{s.vs_id}</td>
                  <td className="py-2.5">{s.name}</td>
                  <td className="py-2.5">{s.class_name}-{s.section}</td>
                  <td className="py-2.5 font-mono text-xs">R{s.room_no}/B{s.bed_no}</td>
                  <td className="py-2.5"><span className="text-xs px-2 py-0.5 rounded-md bg-[var(--vs-success)]/10 text-[var(--vs-success)] border border-[var(--vs-success)]/30 capitalize">{s.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =============== SCHOOL ADMIN ===============
function SchoolAdminHome() {
  const [students, setStudents] = useState([]);
  const [marksheets, setMarksheets] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/students").then((r) => setStudents(r.data)).catch(() => {}),
      api.get("/school/marksheets").then((r) => setMarksheets(r.data)).catch(() => {}),
    ]);
  }, []);

  const byClass = students.reduce((acc, s) => {
    const k = s.class_name || "—";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const classData = Object.entries(byClass).map(([k, v]) => ({ class: `Class ${k}`, students: v }));

  return (
    <DashboardLayout title="School Overview" subtitle="Enrollment, marksheet workflow, fees.">
      <PendingGlance />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat icon={Student} label="Enrolled" value={students.length} sub="Active + draft" />
        <Stat icon={Books} label="Marksheets" value={marksheets.length} sub={`${marksheets.filter((m) => m.status === "published").length} published`} />
        <Stat icon={GraduationCap} label="Classes" value={classData.length} sub={classData.map((c) => c.class.replace("Class ", "")).join(", ") || "—"} />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-5 vs-card">
          <div className="eyebrow">Class-wise headcount</div>
          <div className="font-display text-lg font-semibold mb-3">Distribution</div>
          <div style={CHART_CONTAINER_STYLE}>
            <ResponsiveContainer>
              <BarChart data={classData}>
                <CartesianGrid stroke="var(--vs-border)" strokeDasharray="2 4" />
                <XAxis dataKey="class" tick={CHART_TICK} />
                <YAxis tick={CHART_TICK} />
                <Tooltip />
                <Bar dataKey="students" fill="var(--vs-secondary)" radius={CHART_BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-5 vs-card">
          <div className="eyebrow">Marksheet pipeline</div>
          <div className="space-y-3 mt-3 text-sm">
            {[
              { k: "draft", l: "Draft" },
              { k: "in_review", l: "In review" },
              { k: "published", l: "Published" },
            ].map(({ k, l }) => {
              const c = marksheets.filter((m) => m.status === k).length;
              return (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-[var(--vs-text-secondary)]">{l}</span>
                  <span className="font-mono">{c}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =============== TEACHER ===============
function TeacherHome() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [students, setStudents] = useState([]);
  const [averages, setAverages] = useState(null);

  useEffect(() => {
    api.get("/teacher/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/teacher/students").then((r) => setStudents(r.data)).catch(() => {});
    api.get("/teacher/class-averages").then((r) => setAverages(r.data)).catch(() => {});
  }, []);

  const classes = profile?.classes_assigned || (user?.class_assigned ? [user.class_assigned] : []);
  const byClass = students.reduce((acc, s) => {
    const k = s.class_name || "—";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <DashboardLayout
      title={`${user?.subject || "Subject"} teacher portal`}
      subtitle={`Subject-wise ownership across all your assigned classes${classes.length ? ` (${classes.map(c => `Class ${c}`).join(", ")})` : ""}.`}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat icon={Books} label="Subject" value={user?.subject || "—"} sub={classes.length ? `${classes.length} class${classes.length === 1 ? "" : "es"} assigned` : "No class assigned"} />
        <Stat icon={Student} label="Students under you" value={students.length} sub={Object.entries(byClass).map(([c, n]) => `Class ${c}: ${n}`).join(" · ") || "—"} />
        <div className="p-5 vs-card flex flex-col justify-between">
          <div>
            <div className="eyebrow">Bulk marks entry</div>
            <div className="font-display text-base font-semibold mt-2">Enter {user?.subject || "subject"} marks for an entire class at once</div>
            <p className="text-xs text-[var(--vs-text-secondary)] mt-1">Pick exam → pick class → fill the table → submit. Marksheets auto-created.</p>
          </div>
          <Link
            to="/app/teacher/marks"
            data-testid="goto-marks-entry"
            className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] hover:bg-[var(--vs-secondary-hover)] text-sm transition-all"
          >
            Open marks entry <Sparkle size={14} weight="fill" />
          </Link>
        </div>
      </div>

      <div className="mt-6 vs-card overflow-x-auto">
        <div className="flex items-center justify-between p-5 pb-3">
          <div>
            <div className="eyebrow">Class trends — {profile?.subject || ""}</div>
            <div className="font-display text-lg font-semibold">Average marks per class across exams</div>
          </div>
          <div className="text-[11px] font-mono text-[var(--vs-text-secondary)]">
            {averages?.exams?.length ? `${averages.exams.length} exams · ${averages.classes.length} classes` : "—"}
          </div>
        </div>
        <div className="px-5 pb-5">
          {(() => {
            if (!averages || !averages.exams?.length) {
              return <div className="text-sm text-[var(--vs-text-secondary)] py-8 text-center">Not enough data yet — submit marks for an exam to see the trend.</div>;
            }
            const palette = ["var(--vs-secondary)", "var(--vs-primary)", "var(--vs-info)", "var(--vs-warning)", "var(--vs-success)"];
            const chartData = averages.exams.map((exam, idx) => {
              const row = { exam };
              averages.series.forEach((s) => {
                const pt = s.data[idx];
                row[`Class ${s.class}`] = pt?.avg ?? null;
              });
              return row;
            });
            return (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData}>
                    <CartesianGrid stroke="var(--vs-border)" strokeDasharray="2 4" />
                    <XAxis dataKey="exam" tick={CHART_TICK_XS} />
                    <YAxis domain={CHART_DOMAIN_PCT} tick={CHART_TICK_SM} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {averages.series.map((s, i) => (
                      <Line
                        key={s.class}
                        type="monotone"
                        dataKey={`Class ${s.class}`}
                        stroke={palette[i % palette.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="mt-6 vs-card overflow-x-auto">
        <div className="flex items-center justify-between p-5 pb-3">
          <div>
            <div className="eyebrow">Your students</div>
            <div className="font-display text-lg font-semibold">All classes under you · {students.length} students</div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">Roll</th>
              <th className="px-4 py-3">VS-ID</th>
              <th className="px-4 py-3">Student</th>
              <th className="px-4 py-3">Class · Sec</th>
            </tr>
          </thead>
          <tbody>
            {students.slice(0, 30).map((s) => (
              <tr key={s.id} data-testid={`teacher-student-${s.vs_id}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono text-xs">{s.roll_number || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.vs_id}</td>
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3 text-xs">{s.class_name}-{s.section || ""}</td>
              </tr>
            ))}
            {students.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No active students in your assigned classes.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

// =============== DONOR ===============
function DonorHome() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [photosMap, setPhotosMap] = useState({});
  const [birthdays, setBirthdays] = useState([]);
  const [total, setTotal] = useState({ total_amount: 0, count: 0 });

  useEffect(() => {
    api.get("/donor/me/students").then(async (r) => {
      setStudents(r.data || []);
      const pmap = {};
      const photos = {};
      for (const s of r.data || []) {
        try {
          const p = await api.get(`/hostel/progress?student_id=${s.id}`);
          pmap[s.id] = p.data;
        } catch (e) {
          logger.warn(`progress fetch failed for ${s.id}:`, e?.message || e);
        }
        try {
          const ph = await api.get(`/hostel/photos?student_id=${s.id}`);
          photos[s.id] = ph.data;
        } catch (e) {
          logger.warn(`photos fetch failed for ${s.id}:`, e?.message || e);
        }
      }
      setProgressMap(pmap);
      setPhotosMap(photos);
    });
    api.get("/donor/birthdays").then((r) => setBirthdays(r.data)).catch(() => {});
    api.get("/donor/donations/total").then((r) => setTotal(r.data)).catch(() => {});
  }, []);

  return (
    <DashboardLayout title={`Welcome back, ${user?.name?.split(" ")[0] || ""}.`} subtitle="Your sponsored students, progress, and impact.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat icon={HandHeart} label="Sponsored students" value={students.length} />
        <Stat icon={Receipt} label="Total contribution" value={`₹${(total.total_amount || 0).toLocaleString()}`} sub={`${total.count} donations captured`} />
        <Stat icon={Cake} label="Upcoming birthdays" value={birthdays.length} sub="Next 30 days" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {students.map((s) => {
          const rows = progressMap[s.id] || [];
          const latest = rows[rows.length - 1];
          const radarData = latest ? [
            { dim: "Academic", v: latest.academic },
            { dim: "Discipline", v: latest.discipline },
            { dim: "Sports", v: latest.sports },
            { dim: "Social", v: latest.social },
          ] : [];
          const trend = rows.map((r) => ({ period: r.period, avg: Math.round((r.academic + r.discipline + r.sports + r.social) / 4) }));
          return (
            <div key={s.id} data-testid={`donor-student-${s.vs_id}`} className="p-5 vs-card">
              <div className="flex items-start justify-between">
                <div>
                  <div className="eyebrow">{s.vs_id}</div>
                  <div className="font-display text-xl font-semibold">{s.name}</div>
                  <div className="text-xs text-[var(--vs-text-secondary)]">Class {s.class_name}-{s.section} • {s.gender}</div>
                </div>
                <Link
                  to={`/app/donations?student_id=${s.id}`}
                  className="text-xs px-3 py-1.5 rounded-md border border-[var(--vs-text)] hover:bg-[var(--vs-text)] hover:text-[var(--vs-bg)] transition-all"
                >
                  Sponsor
                </Link>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div style={{ width: "100%", height: 180 }}>
                  <ResponsiveContainer>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="var(--vs-border)" />
                      <PolarAngleAxis dataKey="dim" tick={CHART_TICK_SM} />
                      <Radar dataKey="v" stroke="var(--vs-secondary)" fill="var(--vs-secondary)" fillOpacity={0.35} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ width: "100%", height: 180 }}>
                  <ResponsiveContainer>
                    <AreaChart data={trend}>
                      <CartesianGrid stroke="var(--vs-border)" strokeDasharray="2 4" />
                      <XAxis dataKey="period" tick={CHART_TICK_XS} />
                      <YAxis domain={CHART_DOMAIN_PCT} tick={CHART_TICK_XS} />
                      <Tooltip />
                      <Area type="monotone" dataKey="avg" stroke="var(--vs-primary)" fill="var(--vs-primary)" fillOpacity={0.25} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="mt-3 text-[11px] font-mono text-[var(--vs-text-secondary)]">
                Aadhar: {s.aadhar} • Encrypted at rest
              </div>

              {/* Monthly activity photos */}
              {(photosMap[s.id] || []).length > 0 && (
                <div className="mt-4">
                  <div className="eyebrow mb-2">Monthly activity photos</div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {(photosMap[s.id] || []).slice(0, 8).map((ph) => (
                      <a
                        key={ph.id}
                        href={`${API}/files/photo?path=${encodeURIComponent(ph.storage_path)}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`donor-photo-${ph.id}`}
                        className="block aspect-square rounded-md overflow-hidden border border-[var(--vs-border)] bg-[var(--vs-bg-subtle)] group"
                      >
                        <img
                          src={`${API}/files/photo?path=${encodeURIComponent(ph.storage_path)}`}
                          alt={ph.caption || ph.period}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] font-mono text-[var(--vs-text-secondary)]">
                    {(photosMap[s.id] || []).length} photo{(photosMap[s.id] || []).length === 1 ? "" : "s"} uploaded by school/hostel
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {students.length === 0 && (
          <div className="p-5 vs-card text-sm text-[var(--vs-text-secondary)]">No sponsored students yet.</div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-5 vs-card">
          <div className="flex items-center justify-between">
            <div className="eyebrow">Birthdays in next 30 days</div>
            <Cake size={18} className="text-[var(--vs-primary)]" />
          </div>
          <div className="mt-3 space-y-2 text-sm">
            {birthdays.length === 0 && <div className="text-[var(--vs-text-secondary)]">No birthdays in the window.</div>}
            {birthdays.map((b) => (
              <div key={b.vs_id} className="flex items-center justify-between border-b border-[var(--vs-border)] last:border-b-0 py-2">
                <div>
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-[var(--vs-text-secondary)]">{b.vs_id}</div>
                </div>
                <div className="font-mono text-xs">{b.next_birthday}</div>
              </div>
            ))}
          </div>
        </div>
        <SecurityBadge />
      </div>
    </DashboardLayout>
  );
}

export default function RoleHome() {
  const { user } = useAuth();
  if (!user) return null;
  switch (user.role) {
    case "super_admin": return <SuperAdminHome />;
    case "hostel_admin": return <HostelAdminHome />;
    case "school_admin": return <SchoolAdminHome />;
    case "teacher": return <TeacherHome />;
    case "donor": return <DonorHome />;
    case "parent": return <ParentHomePage />;
    case "sac_admin": return <SacHome />;
    case "trust_admin": return <SuperAdminHome />;  // Same KPI dashboard, scoped server-side.
    default: return null;
  }
}
